import {
  GetMapById,
  GetTaskById,
  GetTaskContentById,
  GetTimerById,
  GetTitleById,
  GetZoneById
} from './assets';
import { GetInventoryById } from './assets/inventories';
import { GameDocument, Overview } from '../../types/game-document';
import { DesignerSettings } from '../../types/game-document/settings';

export const languageOrder = (gameDocument: GameDocument) => {
  const {
    logoResId,
    headerResId,
    titleResId,
    shortDescriptionResId,
    longDescriptionResId
  } = gameDocument.overview as Overview;
  const {
    defaultTaskAvailableIconResId,
    defaultTaskCompletedIconResId,
    defaultFeedbackAllWrongIconResId,
    defaultFeedbackPartialCorrectIconResId,
    defaultFeedbackAllCorrectIconResId,
    defaultAllWrongFeedbackMessageResId,
    defaultPartialCorrectFeedbackMessageResId,
    defaultAllCorrectFeedbackMessageResId
  } = gameDocument.settings.designer as DesignerSettings;
  let mapIds: { label: string; id: string }[] = [];
  let zoneIds: { label: string; id: string }[] = [];
  let taskIds: { label: string; id: string }[] = [];
  let taskContentIds: { label: string; id: string }[] = [];
  let inventoryIds: { label: string; id: string }[] = [];
  let timerIds: { label: string; id: string }[] = [];
  let titleIds: { label: string; id: string }[] = [];

  if (gameDocument) {
    const {
      maps,
      zones,
      tasks,
      taskContents,
      items: inventories,
      timers,
      titles
    } = gameDocument.assets;

    if (maps && maps.length > 0) {
      mapIds = [
        ...maps
          .filter((m) => m.type === 'illustrated')
          .flatMap((i) =>
            [
              {
                label: 'Illustration Map',
                id: i.imageResId as string
              }
            ].map((m) => {
              let name = GetMapById(gameDocument, i.id)?.name ?? '';
              const zoneAsId = gameDocument.rules.worldMap.zones.find(
                (z) => z.mapAssId === i.id
              )?.zoneAssId;

              if (zoneAsId)
                name = GetZoneById(gameDocument, zoneAsId)?.name ?? '';

              return { ...m, subcategory: name };
            })
          )
      ];
    }

    if (zones && zones?.length > 0) {
      zoneIds = [
        ...zones.flatMap((i, idx) =>
          [
            {
              label: `Title`,
              id: i.titleResId as string
            }
          ].map((m) => ({
            ...m,
            subcategory: GetZoneById(gameDocument, i.id)?.name ?? ''
          }))
        )
      ];
    }

    if (tasks && tasks?.length > 0) {
      taskIds = [
        ...tasks.flatMap((i) => {
          const selected = GetTaskById(gameDocument!, i.id);
          return [
            {
              label: `Available Icon`,
              id: i.imageResId as string
            },
            {
              label: `Completed Icon`,
              id: i.completeImageResId as string
            },
            {
              label: `Title`,
              id: i.titleResId as string
            }
          ].map((m) => ({
            ...m,
            subcategory: selected?.name
          }));
        })
      ];
    }

    if (taskContents && taskContents.length > 0) {
      taskContentIds = [
        ...taskContents
          .filter((t) => t.id !== '-')
          .flatMap((i) => {
            const selected = GetTaskContentById(gameDocument, i.id);
            let forms: typeof taskContentIds = [];

            if (selected?.forms && selected.forms.length > 0) {
              forms = selected.forms.flatMap((f, fIdx) => {
                const numberForm = `Form Number - ${fIdx + 1}`;
                let customFeedback: typeof taskContentIds = [];

                if (f.customFeedback) {
                  customFeedback = [
                    ...(!f.customFeedback.isDefaultAllCorrect
                      ? [
                          {
                            label: `All Correct Icon - ${numberForm}`,
                            id:
                              f.customFeedback.allCorrectUrlResId === '' ||
                              f.customFeedback.allCorrectUrlResId === undefined
                                ? 'allCorrectUrlResId'
                                : f.customFeedback.allCorrectUrlResId,
                            ...((f.customFeedback.allCorrectUrlResId === '' ||
                              f.customFeedback.allCorrectUrlResId ===
                                undefined) && {
                              taskContentId: i.id,
                              formId: f.id
                            })
                          },
                          {
                            label: `All Correct Message - ${numberForm}`,
                            id: f.customFeedback
                              .allCorrectMessageResId as string
                          }
                        ]
                      : []),
                    ...(!f.customFeedback.isDefaultPartiallyCorrect
                      ? [
                          {
                            label: `Partially Correct Icon - ${numberForm}`,
                            id:
                              f.customFeedback.partiallyCorrectUrlResId ===
                                '' ||
                              f.customFeedback.partiallyCorrectUrlResId ===
                                undefined
                                ? 'partiallyCorrectUrlResId'
                                : f.customFeedback.partiallyCorrectUrlResId,
                            ...((f.customFeedback.partiallyCorrectUrlResId ===
                              '' ||
                              f.customFeedback.partiallyCorrectUrlResId ===
                                undefined) && {
                              taskContentId: i.id,
                              formId: f.id
                            })
                          },
                          {
                            label: `Partially Correct Message - ${numberForm}`,
                            id: f.customFeedback
                              .partiallyCorrectMessageResId as string
                          }
                        ]
                      : []),
                    ...(!f.customFeedback.isDefaultAllWrong
                      ? [
                          {
                            label: `All Wrong Icon - ${numberForm}`,
                            id:
                              f.customFeedback.allWrongUrlResId === '' ||
                              f.customFeedback.allWrongUrlResId === undefined
                                ? 'allWrongUrlResId'
                                : f.customFeedback.allWrongUrlResId,
                            ...((f.customFeedback.allWrongUrlResId === '' ||
                              f.customFeedback.allWrongUrlResId ===
                                undefined) && {
                              taskContentId: i.id,
                              formId: f.id
                            })
                          },
                          {
                            label: `All Wrong Message - ${numberForm}`,
                            id: f.customFeedback.allWrongMessageResId as string
                          }
                        ]
                      : [])
                  ].map((cf) => ({ ...cf, subcategory: selected.name }));
                }

                return [
                  {
                    label: `Question - ${numberForm}`,
                    id: f.questionResId as string
                  },
                  ...(f.mediaOnlyIdentifierResId
                    ? [
                        {
                          label: `Media Only Identifier - ${numberForm}`,
                          id: f.mediaOnlyIdentifierResId
                        }
                      ]
                    : []),
                  ...(f.answers && f.answers.length > 0
                    ? f.answers.flatMap((a) => [
                        {
                          label: `Answer - ${numberForm}`,
                          id: a.answerResId as string
                        }
                      ])
                    : []),
                  ...(f.medias && f.medias.length > 0
                    ? f.medias.flatMap((m) => [
                        {
                          label: `Media - ${numberForm}`,
                          id: m
                        }
                      ])
                    : []),
                  ...customFeedback
                ].map((cf) => ({ ...cf, subcategory: selected.name }));
              });
            }

            return [
              {
                label: `Title`,
                id: i.titleResId as string
              },
              {
                label: `Premessage`,
                id: i.preMessageResId as string,
                bodyType: i.bodyType
              },
              {
                label: `Intro`,
                id: i.contentResId as string,
                bodyType: i.bodyType
              },
              ...forms
            ].map((cf) => ({ ...cf, subcategory: selected?.name }));
          })
      ];
    }

    if (inventories && inventories?.length > 0) {
      inventoryIds = [
        ...inventories.flatMap((i) => {
          const selected = GetInventoryById(gameDocument, i.id);
          return [
            {
              label: `Image`,
              id: i.imageResId as string
            },
            {
              label: `Title`,
              id: i.titleResId as string
            },
            {
              label: `Summary`,
              id: i.summaryResId as string
            }
          ].map((cf) => ({ ...cf, subcategory: selected?.name }));
        })
      ];
    }

    if (timers && timers?.length > 0) {
      timerIds = [
        ...timers.flatMap((i) =>
          [
            {
              label: `Title`,
              id: i.titleResId as string
            }
          ].map((m) => ({
            ...m,
            subcategory: GetTimerById(gameDocument, i.id)?.name ?? ''
          }))
        )
      ];
    }

    if (titles && titles?.length > 0) {
      titleIds = [
        ...titles.flatMap((i) => {
          const selected = GetTitleById(gameDocument, i.id);
          return [
            {
              label: `Image`,
              id: i.imageResId as string
            },
            {
              label: `Title`,
              id: i.titleResId as string
            },
            {
              label: `Summary`,
              id: i.summaryResId as string
            }
          ].map((t) => ({ ...t, subcategory: selected?.name }));
        })
      ];
    }
  }

  return [
    ...(logoResId !== ''
      ? [{ label: 'Logo', id: logoResId, category: 'Overview' }]
      : []),
    ...(headerResId !== ''
      ? [{ label: 'Header', id: headerResId, category: 'Overview' }]
      : []),
    { label: 'Title', id: titleResId, category: 'Overview' },
    {
      label: 'Description',
      id: shortDescriptionResId,
      category: 'Overview'
    },
    {
      label: 'Information for Player',
      id: longDescriptionResId,
      category: 'Overview'
    },
    ...mapIds.map((t) => ({ ...t, category: 'Maps' })),
    ...zoneIds.map((t) => ({ ...t, category: 'Zones' })),
    ...taskIds.map((t) => ({ ...t, category: 'Tasks' })),
    ...taskContentIds.map((t) => ({ ...t, category: 'Task Contents' })),
    ...inventoryIds.map((t) => ({ ...t, category: 'Inventory' })),
    ...timerIds.map((t) => ({ ...t, category: 'Timers' })),
    ...titleIds.map((t) => ({ ...t, category: 'Titles' })),
    {
      label: 'Default Available Task Icon',
      id: defaultTaskAvailableIconResId,
      category: 'Settings'
    },
    {
      label: 'Default Completed Task Icon',
      id: defaultTaskCompletedIconResId,
      category: 'Settings'
    },
    {
      label: 'Default Feedback All Correct Icon',
      id: defaultFeedbackAllCorrectIconResId,
      category: 'Settings'
    },
    {
      label: 'Default Feedback All Correct Message',
      id: defaultAllCorrectFeedbackMessageResId,
      category: 'Settings'
    },
    {
      label: 'Default Feedback Partial Correct Icon',
      id: defaultFeedbackPartialCorrectIconResId,
      category: 'Settings'
    },
    {
      label: 'Default Feedback Partial Correct Message',
      id: defaultPartialCorrectFeedbackMessageResId,
      category: 'Settings'
    },
    {
      label: 'Default Feedback All Wrong Icon',
      id: defaultFeedbackAllWrongIconResId,
      category: 'Settings'
    },
    {
      label: 'Default Feedback All Wrong Message',
      id: defaultAllWrongFeedbackMessageResId,
      category: 'Settings'
    }
  ];
};
